import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookmarkPlus, CircleHelp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import { toast } from '@/hooks/use-toast';
import { UserTechnique } from '@/types/technique';
import { removeTechnique } from '@/services/userTechniqueService';
import { MyTechniquesSearch } from '@/components/my-techniques/MyTechniquesSearch';
import { MyTechniquesList } from '@/components/my-techniques/MyTechniquesList';
import { EmptyMyTechniques } from '@/components/my-techniques/EmptyMyTechniques';
import { CreateTechniqueDialog } from '@/components/my-techniques/CreateTechniqueDialog';
import { useTechniquesPerPage } from '@/hooks/useTechniquesPerPage';
import { useIsMobile } from '@/hooks/use-mobile';
import { useOfflineUserTechniques } from '@/contexts/offline';
import { usePermissions } from '@/contexts/unified-permissions';


const MyTechniques = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const { itemsPerPage, setItemsPerPage } = useTechniquesPerPage(9, 'myTechniquesPerPage');
  const isMobile = useIsMobile();
  const { user } = usePermissions();

  // Use offline-first user techniques hook
  const {
    data: userTechniques = [],
    loading: isLoading,
    refresh: refetch
  } = useOfflineUserTechniques(user?.id || '', { cacheFirst: true });

  const helpContent = {
    title: "My Techniques Help",
    description: "Welcome to your personal technique collection!",
    content: (
      <>
        <p>This page displays all techniques you've saved or created.</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Search your saved techniques using the search bar</li>
          <li>Create your own custom techniques with the "Create Technique" button</li>
          <li>Browse the technique library to find and save more techniques</li>
          <li>Click on any technique card to view its detailed information</li>
          <li>Remove techniques from your collection with the bookmark icon</li>
        </ul>
        <p>Click outside this dialog or the close button to dismiss.</p>
      </>
    )
  };

  const handleRemoveTechnique = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await removeTechnique(id);
      toast({
        title: "Technique removed",
        description: "Technique has been removed from your collection"
      });
      refetch();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred while removing the technique",
        variant: "destructive"
      });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const filteredTechniques = useMemo(() => {
    if (!userTechniques || !Array.isArray(userTechniques)) {
      return [];
    }

    return userTechniques.filter((userTechnique: UserTechnique) => {
      if (!userTechnique.technique) return false;

      return userTechnique.technique.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
             userTechnique.technique.description.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [userTechniques, searchQuery]);

  return (
    <>
      {/* MainLayout handles MobileHeader, so no need for duplicate header */}
      <div className="space-y-6 pt-10 sm:pt-4 md:pt-8 pb-16">
        {!isMobile && (
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h1 className="text-3xl font-display font-bold tracking-tight inline-flex items-center gap-2">
              My Techniques
              <CircleHelp
                className="h-5 w-5 text-primary cursor-pointer hover:text-primary/80 transition-colors"
                onClick={() => setHelpOpen(true)}
                strokeWidth={2}
              />
            </h1>

            <div className="flex flex-col sm:flex-row gap-2">
              <CreateTechniqueDialog onCreationSuccess={handleRefresh} />
              <Button variant="outline" className="gap-2" asChild>
                <Link to="/techniques">
                  <BookmarkPlus className="h-4 w-4" />
                  Browse Technique Library
                </Link>
              </Button>
            </div>
          </div>
        )}

        {isMobile && (
          <div className="flex flex-row items-center justify-between gap-2 w-full px-4 mb-4">
            <CreateTechniqueDialog
              onCreationSuccess={handleRefresh}
              className="flex-1"
            />
            <Button variant="outline" className="flex-1 gap-2" asChild>
              <Link to="/techniques">
                <BookmarkPlus className="h-4 w-4" />
                Browse Library
              </Link>
            </Button>
          </div>
        )}

        <MyTechniquesSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />

        {isLoading ? (
          <MyTechniquesList
            filteredTechniques={[]}
            onRemoveTechnique={handleRemoveTechnique}
            isLoading={true}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        ) : filteredTechniques.length > 0 ? (
          <MyTechniquesList
            filteredTechniques={filteredTechniques}
            onRemoveTechnique={handleRemoveTechnique}
            isLoading={false}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        ) : (
          <EmptyMyTechniques searchQuery={searchQuery} />
        )}
      </div>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{helpContent.title}</DialogTitle>
            <DialogDescription>
              {helpContent.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {helpContent.content}
          </div>
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button variant="default" size="sm">Got it</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyTechniques;
